import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import SearchInput from "../../components/inputs/SearchInput";
import Flx from "../../components/layout/Flx";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { losBannedAppraisersApi } from "../../api/losBannedAppraisersApi";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import {
  AdminCard,
  AdminScreenShell,
} from "../admin/shared/AdminScreenShell";
import {
  ActionCell,
  actionColumn,
} from "../admin/shared/adminTableActions";
import { A } from "../admin/shared/adminTokens";
import BannedAppraiserForm from "./BannedAppraiserForm";

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v || "");

/** How many loan chips render in the cell before the rest collapse to a count. */
const LOAN_CHIPS_SHOWN = 2;

/**
 * A loan the appraiser already touched. The record carries the loan NUMBER, and
 * the drilldown route is keyed by `_id`, so the click resolves one to the other
 * (see `openLoan`) rather than routing on the number.
 */
const LoanChip = ({ name, onOpen }) => (
  <Chip
    label={name}
    size="small"
    clickable
    onClick={(e) => {
      e.stopPropagation();
      onOpen(name);
    }}
    sx={{
      height: 20,
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      "&:hover": { bgcolor: A.tealSoft, color: A.tealDeep },
    }}
  />
);

LoanChip.propTypes = { name: PropTypes.string, onOpen: PropTypes.func };

/**
 * The affected-loans cell: the first couple of loans as chips, the rest behind
 * an "and N others" that opens a tooltip listing every one. Chips are clickable
 * in both places — the tooltip is the overflow surface, not a read-only summary.
 */
const LoansCell = ({ loans, onOpen }) => {
  if (!Array.isArray(loans) || !loans.length) return "—";
  const shown = loans.slice(0, LOAN_CHIPS_SHOWN);
  const rest = loans.length - shown.length;

  return (
    <Flx ac g={0.5} sx={{ height: "100%", minWidth: 0 }}>
      {shown.map((l) => (
        <LoanChip key={l} name={l} onOpen={onOpen} />
      ))}
      {rest > 0 ? (
        <Tooltip
          placement="top"
          title={
            <Flx wrap g={0.5} sx={{ p: 0.5, maxWidth: 260 }}>
              {loans.map((l) => (
                <LoanChip key={l} name={l} onOpen={onOpen} />
              ))}
            </Flx>
          }
          slotProps={{
            tooltip: {
              sx: { bgcolor: "#fff", border: `1px solid ${A.line}`, boxShadow: A.shadowMd },
            },
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 11.5,
              color: A.muted,
              whiteSpace: "nowrap",
              cursor: "default",
              textDecoration: "underline dotted",
              textUnderlineOffset: 3,
            }}
          >
            and {rest} other{rest === 1 ? "" : "s"}
          </Box>
        </Tooltip>
      ) : null}
    </Flx>
  );
};

LoansCell.propTypes = { loans: PropTypes.array, onOpen: PropTypes.func };

/**
 * `bannedEffectiveDate` is a DISPLAY string, not ISO — the seeded records carry
 * US "M/D/YYYY" and newer ones may carry "YYYY-MM-DD". Normalising to a date-only
 * ISO key gives the column something that sorts chronologically (lexicographic on
 * ISO = chronological) and that `formatDate` can render timezone-stably; sorting
 * the raw string ordered by leading month digit instead.
 */
const bannedDateIso = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!us) return null;
  const [, m, d, y] = us;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

export default function BannedAppraisersScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { getUserPipeline } = usePipelineHook();
  const gridRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [company, setCompany] = useState(""); // "" = every company
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record or null (=add)

  // `/loan/:id` is keyed by the deal's `_id`, NOT its loan number — the record
  // only carries the number, so the pipeline is the lookup table between them.
  // Fetched once on the first chip click and cached (the promise, not just the
  // result, so a double-click can't fire two requests).
  const pipelinePromise = useRef(null);

  const loanIdByNumber = useCallback(() => {
    if (!pipelinePromise.current) {
      pipelinePromise.current = new Promise((resolve) => {
        getUserPipeline({
          onSuccessFn: (data) => {
            const list = Array.isArray(data) ? data : data?.pipeline || [];
            resolve(
              new Map(
                list
                  .filter((l) => l?.loanNumber && l?._id)
                  .map((l) => [String(l.loanNumber).toUpperCase(), l._id]),
              ),
            );
          },
          onFailFn: () => {
            // Don't cache a failure — the next click should retry.
            pipelinePromise.current = null;
            resolve(null);
          },
        });
      });
    }
    return pipelinePromise.current;
  }, [getUserPipeline]);

  const openLoan = useCallback(
    async (name) => {
      const map = await loanIdByNumber();
      if (!map) {
        enqueueSnackbar("Couldn't load the pipeline to find that loan", {
          variant: "error",
        });
        return;
      }
      const id = map.get(String(name).toUpperCase());
      if (!id) {
        enqueueSnackbar(`${name} isn't in your pipeline`, { variant: "warning" });
        return;
      }
      navigate(`/loan/${id}`);
    },
    [loanIdByNumber, navigate, enqueueSnackbar],
  );

  // Company options come from the data, so the filter can't offer a company
  // nobody is banned under.
  const companies = useMemo(
    () =>
      Array.from(
        new Set((rows || []).map((r) => r.company).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  const visibleRows = useMemo(
    () => (company ? (rows || []).filter((r) => r.company === company) : rows || []),
    [rows, company],
  );

  const fetchRows = useCallback(() => {
    setLoading(true);
    losBannedAppraisersApi
      .list()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => {
        setRows([]);
        enqueueSnackbar("Couldn't load banned appraisers", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openAdd = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);
  const openEdit = useCallback((record) => {
    setEditing(record);
    setFormOpen(true);
  }, []);

  const handleRemove = useCallback(
    (record) => {
      const label =
        [record?.firstName, record?.lastName].filter(Boolean).join(" ") ||
        record?.company ||
        "this appraiser";
      if (!window.confirm(`Remove ${label} from the banned list?`)) return;
      losBannedAppraisersApi
        .remove(record._id)
        .then(() => {
          enqueueSnackbar("Removed from banned list", { variant: "success" });
          setRows((prev) => (prev || []).filter((r) => r._id !== record._id));
        })
        .catch((err) =>
          enqueueSnackbar(
            err?.response?.data?.detail || "Couldn't remove appraiser",
            { variant: "error" },
          ),
        );
    },
    [enqueueSnackbar],
  );

  const handleSaved = useCallback((record) => {
    // Upsert the returned record into the local rows (add OR edit).
    setRows((prev) => {
      const list = prev || [];
      const idx = list.findIndex((r) => r._id === record._id);
      if (idx === -1) return [record, ...list];
      const next = list.slice();
      next[idx] = record;
      return next;
    });
    setFormOpen(false);
  }, []);

  const columnDefs = useMemo(
    () => [
      // Leads the table and carries the default sort: the desk reads this list
      // newest-ban-first. (banned-appraisers.md only *suggests* a column order.)
      {
        headerName: "Banned",
        colId: "bannedEffectiveDate",
        width: 130,
        sort: "desc",
        valueGetter: (p) => bannedDateIso(p.data?.bannedEffectiveDate),
        valueFormatter: (p) => formatDate(p.value, "—", "MMM d, yyyy"),
      },
      { headerName: "Last Name", field: "lastName", minWidth: 130, flex: 1 },
      { headerName: "First Name", field: "firstName", minWidth: 120, flex: 1 },
      { headerName: "Company", field: "company", minWidth: 180, flex: 1.4 },
      {
        headerName: "License #",
        field: "appraiserLicenseNumber",
        width: 105,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "State",
        field: "appraiserLicenseState",
        width: 90,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "License Exp.",
        colId: "licenseExpirations",
        minWidth: 130,
        valueGetter: (p) => joinList(p.data?.licenseExpirations),
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Appraisals",
        field: "appraisalCount",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => (p.value == null ? "—" : p.value),
      },
      {
        headerName: "EV/LQ Loans",
        colId: "evLqLoansAffected",
        minWidth: 230,
        flex: 1,
        sortable: false,
        // Chips are a single centred row, not a wrapping block — the overflow
        // lives in the tooltip so the row height stays uniform.
        cellStyle: { display: "flex", alignItems: "center" },
        valueGetter: (p) => joinList(p.data?.evLqLoansAffected),
        cellRenderer: (p) => (
          <LoansCell loans={p.data?.evLqLoansAffected} onOpen={openLoan} />
        ),
      },
      {
        headerName: "Source",
        field: "source",
        minWidth: 180,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      // The admin action-cell convention (ADMIN_UI.md): each control fills its
      // half of the cell, square, so the whole cell is the hit target.
      actionColumn({
        count: 2,
        cellRenderer: (p) => (
          <ActionCell>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => openEdit(p.data)}
              >
                <EditOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemove(p.data)}
              >
                <DeleteOutline sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </ActionCell>
        ),
      }),
    ],
    [openEdit, handleRemove, openLoan],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    }),
    [],
  );

  const getRowId = useCallback((p) => String(p.data?._id), []);

  return (
    // Lives under `/admin/*` now, so it wears the section's chrome rather than
    // its own hand-rolled page head + scroll container.
    <AdminScreenShell
      icon={<GavelOutlined />}
      title="Banned Appraisers"
      count={visibleRows.length}
      loading={loading}
      subtitleMaxWidth="none"
      subtitle="Global registry of appraisers the desk will not accept. Editing here maintains the shared list used to flag valuations during appraisal review."
      actions={
        <>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchRows} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={openAdd}
          >
            Add Appraiser
          </Button>
        </>
      }
    >
      {/* The filters belong with the thing they filter, not up in the page head
          beside the actions that create rows. White on the grey page so the bar
          reads as a control strip rather than part of the background. */}
      <Flx ac g={1.25} wrap sx={{ mb: 1.5 }}>
        <SearchInput
          value={quickFilter}
          onChange={setQuickFilter}
          placeholder="Search name / license…"
          variant="paper"
          sx={{ width: { xs: "100%", sm: 380 } }}
        />
        <TextField
          select
          size="small"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          sx={{ width: 240, bgcolor: A.surface, "& .MuiInputBase-input": { fontSize: 13 } }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All companies</MenuItem>
          {companies.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      </Flx>

      {/* No border on the card: the grid wrapper draws its own, and the two
          stack into a doubled edge. The card keeps the shadow. */}
      <AdminCard dense sx={{ border: "none" }}>
        <AgGridTableWrapper compactTable>
          <AgGridReact
            ref={gridRef}
            rowData={visibleRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            quickFilterText={quickFilter}
            domLayout="autoHeight"
            loading={loading}
          />
        </AgGridTableWrapper>
      </AdminCard>

      <BannedAppraiserForm
        open={formOpen}
        record={editing}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </AdminScreenShell>
  );
}
